import { useMemo, useState } from "react";
import type { InstallSource } from "@limrun/device-install";
import { useDeviceInstallRelay } from "@limrun/device-install/react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type DeviceInstallRelayProps = {
	registryApiUrl?: string;
	registryToken?: string;
	organizationId?: string;
	installSource?: InstallSource;
	disabled?: boolean;
};

type InstallSourceKind = "assetName" | "assetId" | "downloadUrl";

export function DeviceInstallRelay({ registryApiUrl, registryToken, organizationId, installSource, disabled }: DeviceInstallRelayProps) {
	const [sourceKind, setSourceKind] = useState<InstallSourceKind>("assetName");
	const [sourceValue, setSourceValue] = useState("");
	const [error, setError] = useState<string>();
	const [logs, setLogs] = useState<string[]>([]);
	const relay = useDeviceInstallRelay({
		registryApiUrl,
		token: registryToken,
		organizationId,
		log: (message, detail) => addLog(message, detail),
	});
	const effectiveInstallSource = useMemo<InstallSource | undefined>(() => {
		if (installSource) return installSource;
		const value = sourceValue.trim();
		if (!value) return undefined;
		switch (sourceKind) {
			case "assetId":
				return { assetId: value };
			case "downloadUrl":
				return { url: value };
			case "assetName":
				return { assetName: value };
		}
	}, [installSource, sourceKind, sourceValue]);

	function addLog(message: string, detail?: string) {
		const line = detail ? `${message}\n${detail}` : message;
		setLogs((current) => [line, ...current].slice(0, 100));
	}

	async function requestUSBAccess() {
		setError(undefined);
		await relay.requestUSBAccess();
	}

	async function pairBrowser() {
		setError(undefined);
		await relay.pairBrowser();
	}

	async function startInstall() {
		setError(undefined);
		if (!effectiveInstallSource) {
			setError("Choose an asset from registry or provide a download URL before installing.");
			return;
		}
		await relay.startInstallation(effectiveInstallSource);
	}

	const canUseRegistry = !!registryApiUrl;
	const busy = !!relay.busyAction;
	const shownError = error ?? relay.error;

	return (
		<Dialog>
			<DialogTrigger asChild>
				<Button disabled={disabled || !canUseRegistry}>Install to iPhone</Button>
			</DialogTrigger>
			<DialogContent className="max-h-[90vh] max-w-2xl">
				<DialogHeader>
					<DialogTitle>Install to a real iPhone</DialogTitle>
					<DialogDescription>
						Connect a USB device and install an IPA that already exists in asset storage through registry.
					</DialogDescription>
				</DialogHeader>

				{shownError && (
					<Alert variant="destructive">
						<AlertTitle>Install flow failed</AlertTitle>
						<AlertDescription>{shownError}</AlertDescription>
					</Alert>
				)}

				<div className="space-y-4">
					{installSource ? (
						<div className="rounded-md border p-3 text-sm">
							<div className="font-medium">Install source</div>
							<div className="mt-1 break-all text-muted-foreground">{installSourceLabel(installSource)}</div>
						</div>
					) : (
						<div className="grid gap-3 md:grid-cols-[180px_1fr]">
							<label className="space-y-2">
								<Label>Source type</Label>
								<select
									className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
									value={sourceKind}
									onChange={(event) => setSourceKind(event.currentTarget.value as InstallSourceKind)}
								>
									<option value="assetName">Asset name</option>
									<option value="assetId">Asset ID</option>
									<option value="downloadUrl">Download URL</option>
								</select>
							</label>
							<label className="space-y-2">
								<Label>{sourceKind === "downloadUrl" ? "Download URL" : sourceKind === "assetId" ? "Asset ID" : "Asset name"}</Label>
								<Input
									value={sourceValue}
									onChange={(event) => setSourceValue(event.currentTarget.value)}
									placeholder={sourceKind === "downloadUrl" ? "https://..." : sourceKind === "assetId" ? "asset_..." : "builds/MyApp.ipa"}
								/>
							</label>
						</div>
					)}

					<div className="rounded-md border p-3 text-sm">
						<div className="font-medium">Device</div>
						<div className="mt-1 text-muted-foreground">
							{relay.device
								? `${relay.device.hello.productName ?? "iPhone"} ${relay.device.hello.serialNumber}`
								: "No USB device selected."}
						</div>
						<div className="mt-2 text-muted-foreground">
							{relay.hasPairRecord ? "Pair record found in this browser." : "No pair record loaded for this device."}
						</div>
						{relay.pairConfirmationRequired && (
							<div className="mt-2 text-amber-600">
								Unlock the iPhone and accept the pairing prompt, then try pairing again.
							</div>
						)}
					</div>

					<div className="flex flex-wrap gap-2">
						<Button variant="outline" onClick={() => void requestUSBAccess()} disabled={!canUseRegistry || busy}>
							{relay.busyAction === "usb" ? "Selecting..." : "Select USB device"}
						</Button>
						<Button variant="outline" onClick={() => void pairBrowser()} disabled={!relay.canPair}>
							{relay.busyAction === "pair" ? "Pairing..." : "Pair browser"}
						</Button>
						<Button onClick={() => void startInstall()} disabled={!relay.canInstall || !effectiveInstallSource}>
							{relay.busyAction === "install" ? "Installing..." : "Install"}
						</Button>
						<Button variant="ghost" onClick={relay.stopRelay} disabled={!relay.device && logs.length === 0}>
							Stop
						</Button>
					</div>

					{!installSource && (
						<p className="text-xs text-muted-foreground">
							Builds should be triggered from limbuild with upload enabled first. This dialog only installs an
							already-uploaded registry asset.
						</p>
					)}

					{logs.length > 0 && (
						<div className="space-y-2">
							<Label>Install logs</Label>
							<div className="max-h-48 overflow-y-auto rounded-md border bg-muted/40 p-3 font-mono text-xs">
								{logs.map((line, index) => (
									<pre key={`${index}-${line}`} className="mb-2 whitespace-pre-wrap">
										{line}
									</pre>
								))}
							</div>
						</div>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
}

function installSourceLabel(source: InstallSource): string {
	if ("assetId" in source && source.assetId) return `Asset ID: ${source.assetId}`;
	if ("assetName" in source && source.assetName) return `Asset name: ${source.assetName}`;
	if ("url" in source && source.url) return `Download URL: ${source.url}`;
	return "Unknown source";
}
